/**
 * The "Share message" sheet (Slack forward; logic/share): pick one or more conversations I can post in (search by
 * name; tap again to remove; up to MAX_SHARE_DESTINATIONS), add one optional comment, Share — a copy is posted to each.
 * One per workspace shell; opened from a message's menu. Online only: a refused share keeps the sheet open with
 * `failed` set.
 */
import type { Message } from "@bundle/contracts";
import { shareTargets, toggleShareDestination, type ShareDestination } from "../logic/share.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ShareMessageState = {
  /** The message being shared; absent = closed. */
  message?: Message;
  query: string;
  targets: ShareDestination[];
  /** Picked destinations' channel ids, in the order picked. */
  selected: string[];
  /** The picked destinations (the "To" chips), in the order picked. */
  picked: ShareDestination[];
  comment: string;
  sending: boolean;
  failed: boolean;
};

const closed = (): ShareMessageState => ({ query: "", targets: [], selected: [], picked: [], comment: "", sending: false, failed: false });

export class ShareMessageViewModel extends ViewModel<ShareMessageState> {
  constructor(private readonly ws: WorkspaceViewModel) {
    super(closed());
  }

  open(message: Message): void {
    this.setState({ ...closed(), message, targets: this.targets("") });
  }

  close(): void {
    this.setState(closed());
  }

  setQuery(query: string): void {
    this.update({ query, targets: this.targets(query) });
  }

  /** Adds a destination, or removes it when already picked. */
  toggle(channelId: string): void {
    const selected = toggleShareDestination(this.state.selected, channelId);
    this.update({ selected, picked: this.picked(selected), failed: false });
  }

  setComment(comment: string): void {
    this.update({ comment });
  }

  get canSend(): boolean {
    return this.state.message !== undefined && this.state.selected.length > 0 && !this.state.sending;
  }

  /** Shares to every picked destination; resolves with the new messages (undefined when refused or not ready). */
  async send(): Promise<Message[] | undefined> {
    const m = this.state.message;
    const to = this.state.selected;
    if (m === undefined || to.length === 0 || this.state.sending) return undefined;
    this.update({ sending: true, failed: false });
    try {
      const posted = await this.ws.deps.api.shareMessage(this.ws.wsId, m.id, { channelIds: to, text: this.state.comment.trim(), clientId: this.ws.deps.ids.next() });
      this.close();
      return posted;
    } catch {
      this.update({ sending: false, failed: true });
      return undefined;
    }
  }

  private targets(query: string): ShareDestination[] {
    const cache = this.ws.state.cache;
    return cache === undefined ? [] : shareTargets(cache, query);
  }

  private picked(selected: readonly string[]): ShareDestination[] {
    const cache = this.ws.state.cache;
    if (cache === undefined) return [];
    const all = shareTargets(cache, "", Number.MAX_SAFE_INTEGER);
    return selected.flatMap((id) => all.filter((d) => d.channelId === id));
  }
}
